import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  IconButton,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ChatMessageContent from './ChatMessageContent';
import { io, Socket } from 'socket.io-client';
import { getSocketBaseUrl } from '../services/socketService';
import api from '../services/api';

const BOT_AVATAR = (
  <Avatar sx={{ bgcolor: '#7e57c2', width: 32, height: 32 }}>
    <SmartToyIcon sx={{ fontSize: 18, color: '#fff' }} />
  </Avatar>
);
const USER_AVATAR = (
  <Avatar sx={{ bgcolor: '#90caf9', width: 32, height: 32 }}>
    <span role="img" aria-label="user"> </span>
  </Avatar>
);
const HUMAN_AVATAR = (
  <Avatar sx={{ bgcolor: '#b39ddb', width: 32, height: 32 }}>
    <span role="img" aria-label="agent"> </span>
  </Avatar>
);

interface Message {
  from: 'user' | 'bot' | 'human';
  text: string;
  time?: string;
  createdAt?: string;
}

type ChatMode = 'bot' | 'human';

const BOT_GREETING =
  "Hi! I'm the Workflow.Estate assistant. Ask me about our platform, features, plans, free trial, or how to get started.";

const HUMAN_GREETING_AVAILABLE =
  "Hi! How can we help you today? Our team is online and will reply shortly.";

const HUMAN_GREETING_AWAY =
  "Hi! Our team is currently away, but we'll get back to you as soon as possible. Please leave your message below.";

const getWebsiteChatSessionId = (): string => {
  const key = 'website_chat_session';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(key, id);
  }
  return id;
};

const ChatBox = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [mode, setMode] = useState<ChatMode>('bot');
  const [adminAvailable, setAdminAvailable] = useState<boolean | null>(null);
  const [messages, setMessages] = useState<Message[]>([
    { from: 'bot', text: BOT_GREETING, time: '' },
  ]);
  const [form, setForm] = useState({ name: '', email: '' });
  const [formSent, setFormSent] = useState(false);
  const [formValidated, setFormValidated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [botTyping, setBotTyping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactToken, setContactToken] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const formatTime = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleTimeString(navigator.language, { hour: '2-digit', minute: '2-digit' });
  };

  const loadMessages = React.useCallback(async (silent = false) => {
    if (!form.email) return;

    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await api.get(`/customers/messages?email=${encodeURIComponent(form.email)}`);

      if (response.data._id) {
        const backendMessages = response.data.messages || [];
        const initialMsg = {
          from: 'human' as const,
          text: adminAvailable === false ? HUMAN_GREETING_AWAY : HUMAN_GREETING_AVAILABLE,
          time: '',
        };
        const frontendMessages: Message[] = [initialMsg];

        backendMessages.forEach((msg: any) => {
          frontendMessages.push({
            from: msg.isFromCustomer ? 'user' : 'human',
            text: msg.text,
            time: msg.createdAt ? formatTime(msg.createdAt) : '',
            createdAt: msg.createdAt,
          });
        });

        setMessages(frontendMessages);
      }
    } catch (err: any) {
      console.error('Error loading messages:', err);
      if (!silent) {
        setError('Failed to load messages');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [form.email, adminAvailable]);

  useEffect(() => {
    if (!open || mode !== 'human' || !formValidated || !form.email || !contactToken) {
      if (socket) {
        socket.disconnect();
        setSocket(null);
      }
      return;
    }

    const newSocket = io(getSocketBaseUrl(), {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: true,
    });

    newSocket.on('connect', () => {
      newSocket.emit('join_contact_room', { token: contactToken });
    });

    newSocket.on('contact_room_joined', () => {
      loadMessages();
    });

    newSocket.on('contact_room_error', (data) => {
      setError(data.message || 'Failed to join chat room');
    });

    newSocket.on('contact_message', (data) => {
      if (data.type === 'admin_reply' && data.message) {
        const adminMessage: Message = {
          from: 'human',
          text: data.message.text,
          time: data.message.createdAt ? formatTime(data.message.createdAt) : '',
          createdAt: data.message.createdAt,
        };
        setMessages(prev => [...prev, adminMessage]);
      }
    });

    newSocket.on('admin_presence_update', () => {
      api.get('/customers/contact/availability')
        .then(response => {
          setAdminAvailable(response.data.available);
        })
        .catch(err => console.error('Error checking availability:', err));
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
      setSocket(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, formValidated, form.email, contactToken]);

  useEffect(() => {
    const checkAvailability = async () => {
      try {
        const response = await api.get('/customers/contact/availability');
        setAdminAvailable(response.data.available);
      } catch (err) {
        console.error('Error checking admin availability:', err);
        setAdminAvailable(null);
      }
    };

    if (open) {
      checkAvailability();
      const interval = setInterval(checkAvailability, 10000);
      return () => clearInterval(interval);
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, botTyping]);

  const switchToHumanMode = () => {
    setMode('human');
    setMessages([
      {
        from: 'human',
        text: adminAvailable === false ? HUMAN_GREETING_AWAY : HUMAN_GREETING_AVAILABLE,
        time: '',
      },
    ]);
    setError(null);
  };

  const switchToBotMode = () => {
    setMode('bot');
    setMessages([{ from: 'bot', text: BOT_GREETING, time: '' }]);
    setFormValidated(false);
    setContactToken(null);
    setError(null);
  };

  const sendBotMessage = async (messageText: string) => {
    const userMessage: Message = {
      from: 'user',
      text: messageText,
      time: formatTime(new Date()),
    };
    setMessages(prev => [...prev, userMessage]);
    setBotTyping(true);
    setError(null);

    try {
      const history = [...messages, userMessage]
        .filter(m => m.from === 'user' || m.from === 'bot')
        .slice(-12)
        .map(m => ({
          role: m.from === 'user' ? 'user' as const : 'assistant' as const,
          content: m.text,
        }));

      const response = await api.post('/customers/chat/assistant', {
        message: messageText,
        sessionId: getWebsiteChatSessionId(),
        history,
      });

      if (response.data.reply) {
        setMessages(prev => [
          ...prev,
          {
            from: 'bot',
            text: response.data.reply,
            time: formatTime(new Date()),
          },
        ]);
      }
    } catch (err: any) {
      console.error('Error getting bot reply:', err);
      if (err.response?.status === 429) {
        setError(err.response?.data?.message || 'Please wait a moment before sending another message.');
      } else if (err.response?.status === 503) {
        setError(
          err.response?.data?.message ||
            'The assistant is temporarily unavailable. Please try again shortly or email support@workflow.estate.'
        );
      } else {
        setError(err.response?.data?.error || 'Failed to get a reply. Please try again.');
      }
    } finally {
      setBotTyping(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) return;

    const messageText = message;
    setMessage('');

    if (mode === 'bot') {
      await sendBotMessage(messageText);
      return;
    }

    if (!form.name || !form.email) {
      setError('Please provide your name and email before sending a message.');
      setMessage(messageText);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/customers/messages', {
        email: form.email,
        name: form.name,
        message: messageText,
      });

      if (response.data.success) {
        await loadMessages(true);
      }
    } catch (err: any) {
      console.error('Error sending message:', err);

      if (err.response?.status === 429) {
        const retryAfter = err.response?.data?.retryAfter;
        const errorMessage = err.response?.data?.message || 'Please wait a moment before sending another message.';
        setError(errorMessage);

        if (retryAfter && retryAfter > 0) {
          setTimeout(() => {
            setMessage(messageText);
            setError(null);
          }, retryAfter * 1000);
        } else {
          setMessage(messageText);
        }
      } else {
        setError(err.response?.data?.error || 'Failed to send message. Please try again.');
        setMessage(messageText);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    const trimmedName = form.name.trim();
    const trimmedEmail = form.email.trim();

    if (!trimmedName || !trimmedEmail) {
      setError('Please provide both name and email.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please provide a valid email address.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.post('/customers/contact/validate', {
        email: trimmedEmail,
        name: trimmedName,
      });

      if (response.data.success && response.data.token) {
        setForm({ name: trimmedName, email: trimmedEmail });
        setContactToken(response.data.token);
        setFormValidated(true);
      } else {
        setError('Failed to validate contact information. Please try again.');
      }
    } catch (err: any) {
      console.error('Error validating contact info:', err);

      if (err.response?.status === 429) {
        setError(err.response?.data?.message || 'Please wait a moment before trying again.');
      } else {
        setError(err.response?.data?.error || 'Failed to validate contact information. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderAvatar = (from: Message['from']) => {
    if (from === 'bot') return BOT_AVATAR;
    if (from === 'human') return HUMAN_AVATAR;
    return USER_AVATAR;
  };

  const showMessageInput = mode === 'bot' || formValidated;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 1300,
      }}
    >
      {open ? (
        <Paper elevation={8} sx={{ width: 370, borderRadius: 5, overflow: 'hidden', boxShadow: 8 }}>
          <Box sx={{ bgcolor: '#8884', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTopLeftRadius: 20, borderTopRightRadius: 20 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {mode === 'bot' && <SmartToyIcon sx={{ fontSize: 20, color: '#7e57c2' }} />}
              <Typography variant="h6" sx={{ fontWeight: 500, fontSize: 18 }}>
                {mode === 'bot' ? 'Workflow Assistant' : 'Contact Us'}
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ maxHeight: 350, overflowY: 'auto', p: 2, bgcolor: '#f7f7fa' }}>
            <Box ref={messagesContainerRef}>
              {messages.map((msg, idx) => (
                <Box key={idx} sx={{ display: 'flex', mb: 2, flexDirection: msg.from === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-end' }}>
                  {renderAvatar(msg.from)}
                  <Box sx={{ mx: 1, maxWidth: '75%' }}>
                    <Paper
                      sx={{
                        p: 1.5,
                        borderRadius: 3,
                        bgcolor: msg.from === 'user' ? '#757575' : '#fff',
                        color: msg.from === 'user' ? '#fff' : '#444',
                        boxShadow: 0,
                        mb: 0.5,
                        whiteSpace: 'pre-line',
                      }}
                    >
                      <ChatMessageContent
                        text={msg.text}
                        linkColor={msg.from === 'user' ? '#bbdefb' : '#1976d2'}
                      />
                    </Paper>
                    {msg.time && (
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                        {msg.time}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
              {botTyping && (
                <Box sx={{ display: 'flex', mb: 2, alignItems: 'flex-end' }}>
                  {BOT_AVATAR}
                  <Box sx={{ mx: 1 }}>
                    <Paper sx={{ p: 1.5, borderRadius: 3, bgcolor: '#fff', boxShadow: 0 }}>
                      <CircularProgress size={16} />
                    </Paper>
                  </Box>
                </Box>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              <div ref={messagesEndRef} />
            </Box>
          </Box>
          <Divider />
          <Box sx={{ p: 2, bgcolor: '#f7f7fa' }}>
            {!formSent && (
              <>
                {mode === 'bot' && (
                  <Button
                    fullWidth
                    variant="text"
                    size="small"
                    onClick={switchToHumanMode}
                    sx={{ mb: 1.5, textTransform: 'none', color: 'text.secondary' }}
                  >
                    Talk to our team instead
                  </Button>
                )}
                {mode === 'human' && !formValidated && (
                  <>
                    <Button
                      fullWidth
                      variant="text"
                      size="small"
                      onClick={switchToBotMode}
                      sx={{ mb: 1.5, textTransform: 'none', color: 'text.secondary' }}
                    >
                      ← Back to AI assistant
                    </Button>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      Please provide your contact information to message our team:
                    </Typography>
                    <form
                      onSubmit={e => {
                        e.preventDefault();
                        handleFormSubmit(e);
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        label="Name"
                        value={form.name}
                        onChange={e => {
                          setForm(f => ({ ...f, name: e.target.value }));
                          setError(null);
                        }}
                        sx={{ mb: 1.5 }}
                        required
                        autoFocus={!form.name}
                        inputProps={{ maxLength: 100 }}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Email"
                        type="email"
                        value={form.email}
                        onChange={e => {
                          setForm(f => ({ ...f, email: e.target.value }));
                          setError(null);
                        }}
                        sx={{ mb: 1.5 }}
                        required
                        autoFocus={!!form.name && !form.email}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (form.name.trim() && form.email.trim()) {
                              handleFormSubmit(e);
                            }
                          }
                        }}
                        inputProps={{ maxLength: 255 }}
                      />
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        disabled={!form.name.trim() || !form.email.trim() || loading}
                        onClick={(e) => {
                          e.preventDefault();
                          handleFormSubmit(e);
                        }}
                      >
                        Continue
                      </Button>
                    </form>
                  </>
                )}
                {showMessageInput && (
                  <Box>
                    {mode === 'human' && formValidated && (
                      <Button
                        fullWidth
                        variant="text"
                        size="small"
                        onClick={switchToBotMode}
                        sx={{ mb: 1, textTransform: 'none', color: 'text.secondary' }}
                      >
                        ← Back to AI assistant
                      </Button>
                    )}
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Type your message..."
                      value={message}
                      onChange={e => setMessage(e.target.value)}
                      sx={{ mb: 1 }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                      autoFocus
                      disabled={loading || botTyping}
                      inputProps={{ maxLength: mode === 'bot' ? 2000 : undefined }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      disabled={loading || botTyping || !message.trim()}
                      onClick={handleSend}
                    >
                      {loading || botTyping ? <CircularProgress size={20} /> : 'Send'}
                    </Button>
                  </Box>
                )}
              </>
            )}
            {formSent && (
              <Typography variant="body2" color="success.main" align="center">
                Thank you! We'll be in touch soon.
              </Typography>
            )}
          </Box>
        </Paper>
      ) : (
        <Button
          variant="contained"
          color="primary"
          startIcon={<ChatIcon />}
          onClick={() => setOpen(true)}
          sx={{ borderRadius: 5, minWidth: 64, minHeight: 64, boxShadow: 4 }}
        >
          Chat
        </Button>
      )}
    </Box>
  );
};

export default ChatBox;
